<script setup lang="ts">
/**
 * 站台指標的**顯示定義**（`/metric-defs`；批112）。
 *
 * 站台心跳回報的是一包泛型 `{key: number}`——LS 不解讀語意（裁示 26）。這一頁就是
 * 「那些英文鍵在我方後台要印成什麼樣子」的唯一維護處：**出廠空表、人工建、與產品版本
 * 脫鉤**（產品升級不該改我方報表上的字）。
 *
 * ★ 與〈產品 → 憑證設計〉刻意不是同一頁：那邊是產品**宣告**要我方交付什麼（缺了站台
 *   開不起來），這邊是產品回報上來之後我方怎麼**詮釋**（缺了只是難看）。做成頁籤會讓
 *   這條界線消失。
 *
 * ★ 路由 `meta.roles: MANAGER` ＋ 側欄 nav 的 `roles`——後端寫端點是 `require_manager`。
 *   讀端點雖然放寬到 staff（部署頁要拿它翻譯），但這一頁整頁都是寫操作，給 operator
 *   一個必然 403 的入口沒有意義。
 */
import { computed, onMounted, ref } from 'vue'
import { useRoute } from 'vue-router'

import { ApiError } from '@/api/client'
import * as ls from '@/api/ls'
import type { MetricDefinition } from '@/api/ls'
import FilterClearButton from '@/components/FilterClearButton.vue'
import MetricDefEditor from '@/components/MetricDefEditor.vue'
import UiButton from '@/components/UiButton.vue'
import { usePersistedFilters } from '@/lib/persistedFilters'
import { useDataStore } from '@/stores/data'
import { useDialogsStore } from '@/stores/dialogs'
import { vCardTable } from '@/lib/cardTable'

const store = useDataStore()
const dialogs = useDialogsStore()
const route = useRoute()

const rows = ref<MetricDefinition[]>([])
const loading = ref(false)
// 批186：產品過濾記住（前端過濾，清除只要改值）。
const pf = usePersistedFilters('metric-defs', { productFilter: '' })
const { productFilter } = pf.refs
const { dirty: filtersDirty } = pf
const clearFilters = () => pf.clear()

const FORMAT_LABELS: Record<string, string> = {
  integer: '整數',
  decimal: '小數',
  percent: '百分比',
}

const productOptions = computed(() =>
  store.products.map((p) => ({ key: p.key, name: p.name })),
)

const shown = computed(() =>
  productFilter.value ? rows.value.filter((r) => r.product === productFilter.value) : rows.value,
)

async function load() {
  loading.value = true
  try {
    rows.value = await ls.listMetricDefs()
    // store 的快取也跟著換一份，部署頁回頭看時不會是舊的。
    store.metricDefs = rows.value
    store.metricDefsLoaded = true
  } finally {
    loading.value = false
  }
}

onMounted(async () => {
  void store.ensureProducts().catch(() => {})
  await load()
  // 部署頁的「未定義指標 →＋建立顯示定義」帶 `?product=&key=` 進來：直接把新增窗打開
  // 並鎖住那兩欄（發現機制在部署頁，不另開 `seen-keys` 端點——那是無界聚合）。
  const p = typeof route.query.product === 'string' ? route.query.product : ''
  const k = typeof route.query.key === 'string' ? route.query.key : ''
  if (p && k) openCreate(p, k)
})

// --- 編輯視窗 ---
const editorOpen = ref(false)
const editing = ref<MetricDefinition | null>(null)
const presetProduct = ref<string | null>(null)
const presetKey = ref<string | null>(null)

function openCreate(product?: string, key?: string) {
  editing.value = null
  presetProduct.value = product ?? null
  presetKey.value = key ?? null
  editorOpen.value = true
}
function openEdit(row: MetricDefinition) {
  editing.value = row
  presetProduct.value = null
  presetKey.value = null
  editorOpen.value = true
}

async function onSaved() {
  await load()
}

async function remove(row: MetricDefinition) {
  const ok = await dialogs.confirm({
    title: '刪除指標顯示定義',
    message:
      `刪除「${row.label}」（${row.product} / ${row.key}）？` +
      '站台照樣會回報這個指標，只是部署頁會把它列回「未定義指標」並顯示原始英文鍵。' +
      '只是不想看到的話，請改用「隱藏」。',
    danger: true,
    confirmText: '刪除',
  })
  if (!ok) return
  try {
    await ls.deleteMetricDef(row.id)
    await load()
  } catch (e) {
    const detail =
      e instanceof ApiError && e.payload ? (e.payload as { detail?: unknown }).detail : undefined
    await dialogs.notify({
      title: '操作失敗',
      message: typeof detail === 'string' ? detail : '刪除失敗',
    })
  }
}
</script>

<template>
  <div class="page">
    <div class="page-head">
      <h1>指標顯示定義</h1>
      <span class="count">{{ rows.length }}</span>
      <span class="spacer" />
      <label class="hf">
        <span>產品</span>
        <select v-model="productFilter">
          <option value="">全部產品</option>
          <option v-for="p in productOptions" :key="p.key" :value="p.key">{{ p.name }}</option>
        </select>
      </label>
      <FilterClearButton :dirty="filtersDirty" @click="clearFilters" />
      <UiButton variant="primary" @click="openCreate()">＋ 新增定義</UiButton>
    </div>

    <div class="card">
      <p class="subtle">
        站台心跳會回報一包數值指標，<strong>鍵名由產品端自己決定</strong>——這裡只決定它們在
        後台怎麼顯示（中文名、單位、小數位、排序）。沒有定義的鍵照樣會被收下來，在〈部署〉頁的
        「未定義指標」裡以原始英文鍵呈現，可以從那邊直接帶著鍵名過來建立。
      </p>
      <div v-if="loading" class="loading-wrap"><span class="spinner lg" /></div>
      <div v-else class="tbl-scroll" data-hscroll>
        <table v-card-table class="tbl card-table">
          <thead>
            <tr>
              <th>產品</th><th>指標代號</th><th>顯示名稱</th><th>單位</th>
              <th>格式</th><th>小數</th><th>排序</th><th>狀態</th><th></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="r in shown" :key="r.id">
              <td class="subtle nowrap">{{ store.productName(r.product) }}</td>
              <td><code class="mono">{{ r.key }}</code></td>
              <td>
                {{ r.label }}
                <span v-if="r.hint" class="subtle" :title="r.hint">ⓘ</span>
              </td>
              <td class="subtle">{{ r.unit ?? '—' }}</td>
              <td class="nowrap">{{ FORMAT_LABELS[r.value_format] ?? r.value_format }}</td>
              <td class="subtle">{{ r.value_format === 'integer' ? '—' : r.decimals }}</td>
              <td class="subtle">{{ r.sort_order }}</td>
              <td>
                <span class="status-pill" :class="r.hidden ? 'superseded' : 'active'">
                  {{ r.hidden ? '隱藏' : '顯示' }}
                </span>
              </td>
              <td class="acts-cell">
                <div class="acts">
                  <button class="x" title="編輯" @click="openEdit(r)">✎</button>
                  <button class="x danger" title="刪除" @click="remove(r)">✕</button>
                </div>
              </td>
            </tr>
            <tr v-if="!shown.length">
              <td colspan="9" class="subtle empty-row">
                {{ rows.length ? '這個產品還沒有指標顯示定義。' : '還沒有任何指標顯示定義。' }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <MetricDefEditor
      :open="editorOpen"
      :row="editing"
      :preset-product="presetProduct"
      :preset-key="presetKey"
      :products="productOptions"
      @close="editorOpen = false"
      @saved="onSaved"
    />
  </div>
</template>

<style scoped>
.page-head {
  display: flex;
  align-items: center;
  gap: var(--sp-2);
  margin-bottom: var(--sp-4);
  flex-wrap: wrap;
}
.page-head h1 {
  margin: 0;
  font-size: var(--fs-20);
  color: var(--fg);
}
.count {
  font-size: var(--fs-12);
  color: var(--fg-3);
  background: var(--bg);
  padding: 1px 8px;
  border-radius: 999px;
}
.spacer {
  flex: 1;
}
.hf {
  display: flex;
  align-items: center;
  gap: var(--sp-2);
  font-size: var(--fs-12);
  color: var(--fg-3);
  white-space: nowrap;
}
.nowrap {
  white-space: nowrap;
}
.empty-row {
  text-align: center;
  padding: var(--sp-5) 0;
}
/* 表格內的小圖示按鈕（與 `ProductsView`／`CustomersView` 的 `.x` 同一套） */
.x {
  border: none;
  background: none;
  color: var(--fg-4);
  cursor: pointer;
  font-size: var(--fs-13);
  padding: 2px 4px;
  border-radius: var(--r-sm);
}
.x:hover {
  background: var(--bg-hover);
  color: var(--fg);
}
.x.danger:hover {
  color: var(--danger);
}
.mono {
  font-family: ui-monospace, monospace;
}
</style>
